import React, { useEffect, useState } from 'react';
import {
  Box,
  IconButton,
  Typography,
  TextField,
  Button,
  CircularProgress
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import DataTable from 'react-data-table-component';
import { useDispatch, useSelector } from 'react-redux';
import { fetchEmployees, deleteEmployee } from '../../redux/actions/employee/employeeAction';
import { fetchDepartments } from '../../redux/actions/department/departmentAction';
import { confirmDelete, showSuccess } from '../../utils/alertUtils';
import { toast } from 'react-toastify';

const EmployeeTable = ({ onEdit }) => {
  const dispatch = useDispatch();
  const { employees, deleteLoading } = useSelector((state) => state.employee);
  const departments = useSelector((state) => state.department.departments);
  const { user } = useSelector((state) => state.login);
  const [filterText, setFilterText] = useState('');
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    dispatch(fetchEmployees());
    dispatch(fetchDepartments());
  }, [dispatch]);

  const handleDelete = async (id) => {
    const result = await confirmDelete('Are you sure?', 'This will delete the employee permanently!');
    if (result.isConfirmed) {
      setDeletingId(id);
      try {
        await dispatch(deleteEmployee(id));
        showSuccess('Deleted!', 'The employee has been deleted.');
      } catch (error) {
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to delete employee';
        toast.error(errorMessage);
      } finally {
        setDeletingId(null);
      }
    }
  };

  const handleNew = () => {
    onEdit(null); // This will reset the form and show it in "Add" mode
  };

  const getDepartmentName = (id) => {
    const dept = departments.find((d) => d.id === id);
    return dept ? dept.name : '-';
  };

  const filteredEmployees = employees.filter((emp) =>
    emp.emp_code?.toLowerCase().includes(filterText.toLowerCase())
  );

  const columns = [
    { name: <strong>Emp Code</strong>, selector: row => row.emp_code, sortable: true },
    { name: <strong>Name</strong>, selector: row => row.name, sortable: true },
    { name: <strong>Department</strong>, selector: row => getDepartmentName(row.department_id), sortable: true },
    { name: <strong>Status</strong>, selector: row => row.status },
    ...(user?.role === 'Admin' ? [{
      name: <strong>Actions</strong>,
      cell: (row) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton 
            size="small" 
            onClick={() => onEdit(row)}
            disabled={deleteLoading && deletingId === row.id}
          >
            <EditIcon fontSize="small" sx={{ color: '#2196f3' }}/>
          </IconButton>
          <IconButton 
            size="small" 
            onClick={() => handleDelete(row.id)}
            disabled={deleteLoading && deletingId === row.id}
          >
            {deleteLoading && deletingId === row.id ? (
              <CircularProgress size={16} color="error" />
            ) : (
              <DeleteIcon fontSize="small" sx={{ color: '#f44336' }}/>
            )}
          </IconButton>
        </Box>
      ),
    }] : []),
  ];

  return (
    <Box>
      {/* Header Row: Title + Search */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6" fontWeight="bold">
            Employees
          </Typography>
          {user?.role === 'Admin' && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleNew}
              size="small"
              disabled={deleteLoading}
              sx={{ 
                backgroundColor: '#2196f3',
                '&:hover': {
                  backgroundColor: '#1976d2',
                },
              }}
            >
              New
            </Button>
          )}
        </Box>
        <TextField
          size="small"
          placeholder="Search Emp Code..."
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          disabled={deleteLoading}
          sx={{ width: '250px' }}
        />
      </Box>

      <Box sx={{ height: '370px', overflowY: 'auto' }}>
        <DataTable
          columns={columns}
          data={filteredEmployees}
          pagination
          highlightOnHover
          dense
          responsive
          noHeader
          progressPending={deleteLoading}
        />
      </Box>
    </Box>
  );
};

export default EmployeeTable;
